import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { getRoleDisplayName, getUserRoles, isOwnerOrAdmin, isSecretary, isProvider } from '../utils/roleHelpers';
import { planFeatures } from '../utils/planFeatures';
import PaymentAlerts from './PaymentAlerts';
import EspacioPublicitario from './EspacioPublicitario';
import api, { getApiUrl } from '../services/api';
import { normalizeDeporteSlug } from '../utils/deporteSlug';
import Modal from './Modal';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const { data: disciplines } = useQuery(
    ['layout-disciplines', user?.tenant?.id],
    () => api.get('/disciplines').then((r) => r.data),
    { enabled: Boolean(user?.tenant?.id), staleTime: 5 * 60 * 1000 }
  );

  const deporteSlug = useMemo(() => {
    const active = (disciplines || []).filter((d) => d.is_active !== false);
    if (active.length === 0) {
      return 'general';
    }
    return normalizeDeporteSlug(active[0].name);
  }, [disciplines]);
  
  // Detectar si es móvil y manejar resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Cerrar sidebar en móviles cuando se hace clic en un enlace
  const handleNavClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleLogout = () => {
    // Mostrar modal de confirmación antes de cerrar sesión
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Determinar qué menú mostrar según el rol
  const menuItems = useMemo(() => {
    if (!user) return [];
    
    // Menú para proveedores publicitarios
    const providerMenuItems = [
      { path: '/provider-ads', label: 'Mis anuncios', icon: '📢' },
      { path: '/profile', label: 'Mi Perfil', icon: '👤' },
    ];

    // Menú para superadmin (solo gestión del sistema)
    const superadminMenuItems = [
      { path: '/', label: 'Dashboard', icon: '📊' },
      { path: '/profile', label: 'Mi Perfil', icon: '👤' },
      { type: 'separator', label: 'Gestión' },
      { path: '/tenants', label: 'Organizaciones', icon: '🏢' },
      { path: '/users', label: 'Usuarios', icon: '👥' },
    ];

    // Menú completo para dueños y administradores
    // Orden lógico: Dashboard → Configuración → Personal → Estudiantes → Operaciones → Reportes
    const adminMenuItems = [
      { path: '/', label: 'Dashboard', icon: '📊' },
      { path: '/profile', label: 'Mi Perfil', icon: '👤' },
      { type: 'separator', label: 'Configuración' },
      { path: '/disciplines', label: 'Disciplinas', icon: '⚽' },
      { path: '/levels', label: 'Niveles', icon: '📈' },
      { path: '/groups', label: 'Grupos', icon: '👨‍🏫' },
      { path: '/schedules', label: 'Horarios', icon: '🕐' },
      {
        path: '/performance',
        label: 'Pruebas',
        icon: '⏱️',
        submenu: [
          { path: '/performance/dashboard', label: 'Dashboard', icon: '📊' },
          { path: '/performance', label: 'Configurar', icon: '⚙️' },
          { path: '/performance/results', label: 'Resultados', icon: '📋' },
        ],
      },
      { type: 'separator', label: 'Personal' },
      { path: '/teachers', label: 'Personal', icon: '👥' },
      { type: 'separator', label: 'Estudiantes' },
      { path: '/students', label: 'Alumnos', icon: '👥' },
      { path: '/enrollments', label: 'Matrículas', icon: '📝' },
      { type: 'separator', label: 'Operaciones' },
      { path: '/attendances', label: 'Asistencias', icon: '✅' },
      { path: '/payments', label: 'Pagos', icon: '💰' },
      { type: 'separator', label: 'Reportes' },
      { 
        path: '/reports', 
        label: 'Reportes', 
        icon: '📊',
        submenu: [
          { path: '/reports/payments', label: 'Reporte de Pagos', icon: '💰' },
          { path: '/reports/financial', label: 'Reporte Financiero', icon: '📈' },
          { path: '/reports/students', label: 'Lista de Estudiantes', icon: '👥' },
          { path: '/reports/attendances', label: 'Reporte de Asistencias', icon: '✅' }
        ]
      },
    ];

    // Menú para dueños del negocio (incluye sedes)
    const ownerMenuItems = [
      { path: '/', label: 'Dashboard', icon: '📊' },
      { path: '/profile', label: 'Mi Perfil', icon: '👤' },
      { type: 'separator', label: 'Configuración' },
      { path: '/locations', label: 'Sedes', icon: '📍' },
      { path: '/disciplines', label: 'Disciplinas', icon: '⚽' },
      { path: '/levels', label: 'Niveles', icon: '📈' },
      { path: '/groups', label: 'Grupos', icon: '👨‍🏫' },
      { path: '/schedules', label: 'Horarios', icon: '🕐' },
      {
        path: '/performance',
        label: 'Pruebas',
        icon: '⏱️',
        submenu: [
          { path: '/performance/dashboard', label: 'Dashboard', icon: '📊' },
          { path: '/performance', label: 'Configurar', icon: '⚙️' },
          { path: '/performance/results', label: 'Resultados', icon: '📋' },
        ],
      },
      { type: 'separator', label: 'Personal' },
      { path: '/teachers', label: 'Personal', icon: '👥' },
      { type: 'separator', label: 'Estudiantes' },
      { path: '/students', label: 'Alumnos', icon: '👥' },
      { path: '/enrollments', label: 'Matrículas', icon: '📝' },
      { type: 'separator', label: 'Operaciones' },
      { path: '/attendances', label: 'Asistencias', icon: '✅' },
      { path: '/payments', label: 'Pagos', icon: '💰' },
      { type: 'separator', label: 'Reportes' },
      { 
        path: '/reports', 
        label: 'Reportes', 
        icon: '📊',
        submenu: [
          { path: '/reports/payments', label: 'Reporte de Pagos', icon: '💰' },
          { path: '/reports/financial', label: 'Reporte Financiero', icon: '📈' },
          { path: '/reports/students', label: 'Lista de Estudiantes', icon: '👥' },
          { path: '/reports/attendances', label: 'Reporte de Asistencias', icon: '✅' }
        ]
      },
    ];

    // Menú para secretarios (orden lógico para su flujo de trabajo)
    const secretaryMenuItems = [
      { path: '/', label: 'Dashboard', icon: '📊' },
      { path: '/profile', label: 'Mi Perfil', icon: '👤' },
      { type: 'separator', label: 'Estudiantes' },
      { path: '/students', label: 'Alumnos', icon: '👥' },
      { path: '/enrollments', label: 'Matrículas', icon: '📝' },
      { type: 'separator', label: 'Operaciones' },
      { path: '/payments', label: 'Pagos', icon: '💰' },
      { type: 'separator', label: 'Consulta' },
      { path: '/availability', label: 'Disponibilidad', icon: '📋' },
      { path: '/groups', label: 'Grupos', icon: '👨‍🏫' },
      {
        path: '/performance/dashboard',
        label: 'Pruebas',
        icon: '⏱️',
        submenu: [
          { path: '/performance/dashboard', label: 'Dashboard', icon: '📊' },
          { path: '/performance/results', label: 'Resultados', icon: '📋' },
        ],
      },
    ];

    // Menú para profesores
    const teacherMenuItems = [
      { path: '/', label: 'Dashboard', icon: '📊' },
      { path: '/profile', label: 'Mi Perfil', icon: '👤' },
      { path: '/groups', label: 'Mis Grupos', icon: '👨‍🏫' },
      { path: '/attendances', label: 'Asistencias', icon: '✅' },
      {
        path: '/performance/dashboard',
        label: 'Pruebas',
        icon: '⏱️',
        submenu: [
          { path: '/performance/dashboard', label: 'Dashboard', icon: '📊' },
          { path: '/performance/results', label: 'Resultados', icon: '📋' },
        ],
      },
    ];
    
    // Si es proveedor (sin superadmin), menú mínimo
    const hidePerformance = planFeatures(user).performance === false;
    const visibleMenu = (items) => (
      hidePerformance
        ? items.map((item) => (
          String(item.path || '').startsWith('/performance')
            ? { ...item, premiumLocked: true }
            : item
        ))
        : items
    );

    if (isProvider(user) && !user.is_superuser) {
      return providerMenuItems;
    }

    const roleNames = getUserRoles(user);
    if (roleNames.includes('owner')) {
      return visibleMenu(ownerMenuItems);
    }
    if (roleNames.includes('admin')) {
      return visibleMenu(adminMenuItems);
    }
    if (roleNames.includes('secretary')) {
      return visibleMenu(secretaryMenuItems);
    }
    if (roleNames.includes('teacher')) {
      return visibleMenu(teacherMenuItems);
    }

    if (user.is_superuser) {
      return superadminMenuItems;
    }
    
    // Por defecto, menú básico
    return [
      { path: '/', label: 'Dashboard', icon: '📊' },
      { path: '/profile', label: 'Mi Perfil', icon: '👤' },
      { path: '/attendances', label: 'Asistencias', icon: '✅' },
    ];
  }, [user]);

  return (
    <div className="layout">
      {isMobile && (
        <div 
          className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          {sidebarOpen ? (
            <div className="sidebar-logo-container">
              <img 
                src="/logo-seven.png" 
                alt="SEVEN Gestión Deportiva" 
                className="sidebar-logo"
                onError={(e) => {
                  // Si el logo no existe, mostrar texto alternativo
                  e.target.style.display = 'none';
                  const textFallback = e.target.parentElement.querySelector('.sidebar-logo-text');
                  if (textFallback) {
                    textFallback.style.display = 'flex';
                  }
                }}
              />
              <div className="sidebar-logo-text" style={{display: 'none'}}>
                <span className="logo-brand">SEVEN</span>
                <span className="logo-subtitle">Gestión Deportiva</span>
              </div>
            </div>
          ) : (
            <div className="sidebar-logo-icon">
              <img 
                src="/logo-seven.png" 
                alt="SEVEN" 
                className="sidebar-icon-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.logo-7');
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              <span className="logo-7" style={{display: 'none'}}>7</span>
            </div>
          )}
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Colapsar menú' : 'Expandir menú'}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item, index) => {
            if (item.type === 'separator') {
              return (
                <div key={`separator-${index}`} className="nav-separator">
                  {sidebarOpen && <span className="nav-separator-label">{item.label}</span>}
                </div>
              );
            }
            
            // Si el item tiene submenú
            if (item.submenu) {
              const onSection =
                location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`);
              const isActive = onSection;
              const isExpanded =
                expandedMenus[item.path] === undefined ? onSection : Boolean(expandedMenus[item.path]);
              
              return (
                <div key={item.path} className="nav-item-with-submenu">
                  <div
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    onClick={() =>
                      setExpandedMenus((prev) => ({
                        ...prev,
                        [item.path]: !isExpanded,
                      }))
                    }
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {sidebarOpen && (
                      <>
                        <span className="nav-label">{item.label}</span>
                        {item.premiumLocked && <span className="nav-premium-badge">Premium</span>}
                        <span className="nav-arrow">{isExpanded ? '▼' : '▶'}</span>
                      </>
                    )}
                  </div>
                  {sidebarOpen && isExpanded && (
                    <div className="nav-submenu">
                      {item.submenu.map((subitem) => (
                        <Link
                          key={subitem.path}
                          to={subitem.path}
                          className={`nav-subitem ${location.pathname === subitem.path ? 'active' : ''}`}
                          onClick={handleNavClick}
                        >
                          <span className="nav-icon">{subitem.icon}</span>
                          <span className="nav-label">{subitem.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <span className="nav-icon">{item.icon}</span>
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {user?.tenant?.id && sidebarOpen && (
            <EspacioPublicitario deporte={deporteSlug} className="sidebar-ad" />
          )}
          {sidebarOpen ? (
            <button className="btn-logout" onClick={handleLogout}>
              Cerrar Sesión
            </button>
          ) : (
            <button className="btn-logout btn-logout-icon" onClick={handleLogout} title="Cerrar Sesión">
              🚪
            </button>
          )}
        </div>
      </aside>
      <main className="main-content">
        <header className="top-header">
          <button
            className="mobile-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <h1>
            {(() => {
              for (const item of menuItems) {
                if (item.submenu) {
                  const sub = item.submenu.find((entry) => entry.path === location.pathname);
                  if (sub) return sub.label;
                }
                if (item.path && item.path === location.pathname) return item.label;
              }
              return 'Dashboard';
            })()}
          </h1>
          <div className="header-actions">
            {(isOwnerOrAdmin(user) || isSecretary(user)) && <PaymentAlerts />}
            {user?.tenant?.logo_url && (
              <img 
                src={`${getApiUrl()}/tenants/${user.tenant.id}/logo?v=${encodeURIComponent(user.tenant.updated_at || new Date().toISOString())}`} 
                alt="Logo" 
                className="header-tenant-logo"
              />
            )}
            <div className="header-user-info">
              <span className="header-user-name">{getRoleDisplayName(user)}</span>
              <span className="header-tenant-name">{user?.tenant?.name || user?.tenant_name || 'Plataforma'}</span>
            </div>
          </div>
        </header>
        <div className="content">{children}</div>
      </main>

      {/* Modal de confirmación para cerrar sesión */}
      <Modal
        isOpen={showLogoutModal}
        onClose={cancelLogout}
        title="Cerrar Sesión"
        closeOnOverlayClick={true}
      >
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '20px', fontSize: '16px' }}>
            ¿Seguro que deseas cerrar sesión?
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              className="btn btn-secondary"
              onClick={cancelLogout}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={confirmLogout}
              style={{ backgroundColor: '#e74c3c', borderColor: '#e74c3c' }}
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Layout;

